import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ClipboardList, Home, Sparkles, UserCircle, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import KindredAnnouncementSurface from "@/components/KindredAnnouncementSurface";
import {
  KindredLogoButton,
  NotificationsButton,
} from "@/components/MemberHeaderControls";
import { useUnreadNotificationCount } from "@/hooks/useUnreadNotificationCount";
import {
  announcementDismissKey,
  KindredAnnouncement,
  pickActiveAnnouncement,
} from "@/lib/announcements";
import { cn } from "@/lib/utils";
import {
  AppContentContainer,
  AppHeroShell,
  AppShell as FoundationAppShell,
  AppShellMain,
  AppTabBar,
  AppTabButton,
} from "../../ui-foundation";

const tabs = [
  { path: "/kinfeed", label: "Kinfeed", icon: Home },
  { path: "/communities", label: "Community", icon: Users },
  { path: "/activities", label: "Activities", icon: Sparkles },
  { path: "/support", label: "Support", icon: ClipboardList },
  { path: "/my-kindred", label: "Menu", icon: UserCircle },
];

const titleMap: Record<string, string> = {
  "/kinfeed": "Kinfeed",
  "/communities": "Community",
  "/activities": "Activities",
  "/support": "Support",
  "/my-kindred": "Menu",
};

const subtitleMap: Record<string, string> = {
  "/kinfeed": "The front door to Kindred, with the latest conversation and easy ways to join in.",
  "/communities": "Channels, chat rooms, and messages in one clear place.",
  "/activities": "Group plans and What's On, together in one activity hub.",
  "/support": "Practical help without sounding like a leaflet.",
  "/my-kindred": "Your profile, support and account options.",
};

const inferActiveTab = (pathname: string) => {
  if (pathname.startsWith("/community/feed") || pathname === "/kinfeed") return tabs[0];
  if (
    pathname.startsWith("/communities") ||
    pathname.startsWith("/community") ||
    pathname.startsWith("/messages")
  ) return tabs[1];
  if (
    pathname.startsWith("/activities") ||
    pathname.startsWith("/adventures") ||
    pathname.startsWith("/whats-on")
  ) return tabs[2];
  if (pathname.startsWith("/support")) return tabs[3];
  if (
    pathname.startsWith("/my-kindred") ||
    pathname.startsWith("/my-adventures") ||
    pathname.startsWith("/settings") ||
    pathname.startsWith("/notifications") ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/moderation-guidelines") ||
    pathname.startsWith("/privacy") ||
    pathname.startsWith("/terms") ||
    pathname.startsWith("/conduct") ||
    pathname.startsWith("/about-kindred") ||
    pathname.startsWith("/data-rights") ||
    pathname.startsWith("/contact-complaints") ||
    pathname.startsWith("/editorial-policy") ||
    pathname.startsWith("/child-safety")
  ) return tabs[4];

  return tabs[0];
};

const AppShell = ({ children }: { children?: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const unreadNotifs = useUnreadNotificationCount();
  const [announcement, setAnnouncement] = useState<KindredAnnouncement | null>(null);
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const activeTab = useMemo(
    () => inferActiveTab(location.pathname),
    [location.pathname],
  );
  const title = titleMap[activeTab.path] || "Kindred";
  const subtitle = subtitleMap[activeTab.path];

  const dismissAnnouncement = useCallback(() => {
    if (!user?.id || !announcement) {
      setAnnouncementOpen(false);
      return;
    }

    localStorage.setItem(announcementDismissKey(user.id, announcement), "1");
    setAnnouncementOpen(false);
  }, [announcement, user?.id]);

  const handleAnnouncementAction = useCallback(() => {
    if (!announcement) return;

    const destination = announcement.cta_url_or_route?.trim();
    if (destination) {
      if (destination.startsWith("/")) {
        navigate(destination);
      } else {
        window.open(destination, "_blank", "noopener,noreferrer");
      }
    }

    if (announcement.dismissible || !destination) {
      dismissAnnouncement();
    }
  }, [announcement, dismissAnnouncement, navigate]);

  useEffect(() => {
    if (!user?.id) {
      setAnnouncement(null);
      setAnnouncementOpen(false);
      return;
    }

    let active = true;

    const loadAnnouncement = async () => {
      const { data } = await supabase
        .from("announcements" as any)
        .select("*")
        .eq("is_active", true)
        .order("updated_at", { ascending: false })
        .limit(10);

      if (!active) return;

      const nextAnnouncement = pickActiveAnnouncement((data ?? []) as KindredAnnouncement[], role);
      if (!nextAnnouncement) {
        setAnnouncement(null);
        setAnnouncementOpen(false);
        return;
      }

      if (localStorage.getItem(announcementDismissKey(user.id, nextAnnouncement))) {
        setAnnouncement(null);
        setAnnouncementOpen(false);
        return;
      }

      setAnnouncement(nextAnnouncement);
      setAnnouncementOpen(true);
    };

    void loadAnnouncement();

    const handleFocus = () => {
      void loadAnnouncement();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        void loadAnnouncement();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      active = false;
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [role, user?.id]);

  return (
    <FoundationAppShell minHeight="100vh">
      <header className="relative z-20">
        <AppContentContainer pt="$xs">
          <div className="space-y-2">
            <AppHeroShell
              title={title}
              leading={<KindredLogoButton />}
              action={<NotificationsButton unreadCount={unreadNotifs} />}
            />
            {subtitle ? <p className="text-sm leading-5 text-muted-foreground">{subtitle}</p> : null}
          </div>
        </AppContentContainer>
      </header>

      <div className="relative z-20">
        <AppContentContainer pt="$xs">
          <KindredAnnouncementSurface
            announcement={announcement}
            open={announcementOpen}
            onDismiss={dismissAnnouncement}
            onAction={handleAnnouncementAction}
          />
        </AppContentContainer>
      </div>

      <AppShellMain position="relative" zIndex="$base">
        <AppContentContainer flex={1} pt="$xs" pb="$xxxl">
          <div className="w-full animate-fade-in pb-[5rem] sm:pb-[5.5rem]">
            {children ?? <Outlet />}
          </div>
        </AppContentContainer>
      </AppShellMain>

      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 px-3 pb-3 pb-safe sm:px-6">
        <div className="pointer-events-auto mx-auto w-full max-w-xl">
          <AppTabBar>
            {tabs.map((tab) => {
              const isActive = location.pathname.startsWith(tab.path);
              const Icon = tab.icon;
              const showBadge = tab.path === "/my-kindred" && unreadNotifs > 0;

              return (
                <AppTabButton
                  key={tab.path}
                  active={isActive}
                  onPress={() => navigate(tab.path)}
                  accessibilityLabel={tab.label}
                >
                  <span
                    className={cn(
                      "relative flex min-w-0 w-full flex-col items-center justify-center gap-1 rounded-[20px] px-1 py-2.5 transition-all sm:flex-row sm:gap-2 sm:px-3 sm:py-3",
                      isActive ? "text-primary" : "text-muted-foreground hover:bg-primary/8 hover:text-primary",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors",
                        isActive ? "bg-primary/10 text-primary" : "text-primary/80",
                      )}
                    >
                      <Icon size={20} strokeWidth={isActive ? 2.3 : 1.9} />
                    </span>
                    <span className="max-w-full truncate text-[10px] font-semibold leading-none tracking-[0.01em] sm:text-xs">
                      {tab.label}
                    </span>
                    {showBadge ? <span className="absolute right-3 top-2 h-2 w-2 rounded-full bg-accent" /> : null}
                  </span>
                </AppTabButton>
              );
            })}
          </AppTabBar>
        </div>
      </div>
    </FoundationAppShell>
  );
};

export default AppShell;
